import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import {
    AssetStatus,
    DEFAULT_TEMPLATE_ACCENT_COLOR,
    ImageItem,
    LayerItem,
    SceneV2,
    TemplateDimensions,
    TemplateDto,
    TemplateOrientation,
    TemplateSceneV2,
    VideoItem,
    VideoMimeType,
    WebsiteItem,
    TemplateStyle,
    oppositeOrientation,
    rescaleScenes,
    transposeDimensions,
} from "@exibra/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    ChevronLeft,
    ChevronRight,
    Film,
    Globe2,
    Image as ImageIcon,
    Link,
    Plus,
    Trash2,
    Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { templateService } from "@/services/template.service";
import { toast } from "sonner";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { AccentColorField } from "../../components/accent-color-field/accent-color-field";

interface Props {
    template: TemplateDto;
    onSave: (template: TemplateDto) => void;
}

/** A single orientation's editable layout (primary or alternate). */
interface EditorLayout {
    orientation: TemplateOrientation;
    dimensions: TemplateDimensions;
    scenes: SceneV2[];
}

/**
 * v2 slideshow editor.
 *
 * Layout: a height-capped preview canvas with an inline upload affordance, a right-side
 * scene inspector (active-scene properties), a horizontal scene strip below, and a
 * thin top bar with the template name and save action.
 */
export const TemplateEditorV2Slideshow = ({ template, onSave }: Props) => {
    const { t } = useTranslation();
    const tenant = useTenantStore((state) => state.selectedTenant);
    const initialScene = template.scene as TemplateSceneV2;
    const [name, setName] = useState(template.name);
    // Each orientation is a self-contained layout. The primary layout's orientation is fixed at
    // creation; the alternate (when present) holds the opposite orientation and is edited separately.
    const [primary, setPrimary] = useState<EditorLayout>(() => ({
        orientation: initialScene.orientation,
        dimensions: initialScene.dimensions,
        scenes: initialScene.scenes,
    }));
    const [alternate, setAlternate] = useState<EditorLayout | null>(() =>
        initialScene.alternate
            ? {
                  orientation: initialScene.alternate.orientation,
                  dimensions: initialScene.alternate.dimensions,
                  scenes: initialScene.alternate.scenes,
              }
            : null,
    );
    const [activeOrientation, setActiveOrientation] = useState<TemplateOrientation>(
        initialScene.orientation,
    );
    const [defaultDuration, setDefaultDuration] = useState(initialScene.defaultPhotoDurationMs);
    const [accentColor, setAccentColor] = useState(
        initialScene.style?.accentColor ?? DEFAULT_TEMPLATE_ACCENT_COLOR,
    );
    const [activeIndex, setActiveIndex] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [websiteUrl, setWebsiteUrl] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Per-asset processing status (videos transcode in the background after upload).
    const [assetStatus, setAssetStatus] = useState<Record<string, AssetStatus>>({});

    const isEditingPrimary = activeOrientation === primary.orientation;
    const activeLayout = isEditingPrimary ? primary : (alternate as EditorLayout);
    const scenes = activeLayout.scenes;
    const dimensions = activeLayout.dimensions;
    const activeScene = scenes[activeIndex];

    // Apply a scenes update to whichever layout is currently active.
    const setActiveScenes = (updater: (prev: SceneV2[]) => SceneV2[]) => {
        if (isEditingPrimary) {
            setPrimary((p) => ({ ...p, scenes: updater(p.scenes) }));
        } else {
            setAlternate((a) => (a ? { ...a, scenes: updater(a.scenes) } : a));
        }
    };

    // Collect cacheKeys of video layers (across both layouts) marked "processing" so we can poll.
    const processingKeys = useMemo(() => {
        const keys: string[] = [];
        for (const list of [primary.scenes, alternate?.scenes ?? []]) {
            for (const scene of list) {
                for (const el of scene.elements) {
                    if (el.type === "video") {
                        const ck = (el as VideoItem).cacheKey;
                        if (ck && assetStatus[ck] === "processing") keys.push(ck);
                    }
                }
            }
        }
        return keys;
    }, [primary, alternate, assetStatus]);

    useEffect(() => {
        if (!tenant || processingKeys.length === 0) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const statuses = await templateService.getAssetStatuses(tenant.id, processingKeys);
                if (cancelled) return;
                setAssetStatus((prev) => {
                    const next = { ...prev };
                    for (const s of statuses) {
                        if (s.status) next[s.cacheKey] = s.status;
                    }
                    return next;
                });
            } catch {
                /* transient — keep polling */
            }
        };
        const interval = setInterval(poll, 3000);
        void poll();
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [tenant, processingKeys]);

    const updateScene = (index: number, updater: (scene: SceneV2) => SceneV2) => {
        setActiveScenes((prev) => prev.map((scene, i) => (i === index ? updater(scene) : scene)));
    };

    const switchOrientation = (orientation: TemplateOrientation) => {
        setActiveOrientation(orientation);
        setActiveIndex(0);
    };

    // Seed the opposite-orientation layout by auto-rescaling the primary layout's scenes to the
    // transposed dimensions, then switch to it for fine-tuning.
    const handleAddVersion = () => {
        if (alternate) return;
        const orientation = oppositeOrientation(primary.orientation);
        const targetDimensions = transposeDimensions(primary.dimensions);
        setAlternate({
            orientation,
            dimensions: targetDimensions,
            scenes: rescaleScenes(primary.scenes, primary.dimensions, targetDimensions),
        });
        setActiveOrientation(orientation);
        setActiveIndex(0);
    };

    const handleRemoveVersion = () => {
        setAlternate(null);
        setActiveOrientation(primary.orientation);
        setActiveIndex(0);
    };

    const handleAddScene = () => {
        const next: SceneV2 = {
            id: uuidv4(),
            name: `${t("templates.scene", { defaultValue: "Scene" })} ${scenes.length + 1}`,
            durationMs: defaultDuration,
            elements: [],
        };
        setActiveScenes((prev) => [...prev, next]);
        setActiveIndex(scenes.length);
    };

    const handleRemoveScene = (index: number) => {
        if (scenes.length === 1) {
            toast.error(
                t("templates.scene.lastSceneError", {
                    defaultValue: "At least one scene is required",
                }),
            );
            return;
        }
        setActiveScenes((prev) => prev.filter((_, i) => i !== index));
        setActiveIndex((current) => Math.max(0, current - (current >= index ? 1 : 0)));
    };

    const handleReorder = (from: number, to: number) => {
        if (to < 0 || to >= scenes.length) return;
        setActiveScenes((prev) => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            if (!moved) return prev;
            next.splice(to, 0, moved);
            return next;
        });
        setActiveIndex(to);
    };

    const triggerFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleUploadMedia = async (file: File) => {
        if (!tenant || !activeScene) return;
        try {
            setUploading(true);
            const asset = await templateService.uploadAsset(file, tenant.id);
            const isVideo = asset.mimeType.startsWith("video/");
            if (asset.status) {
                setAssetStatus((prev) => ({ ...prev, [asset.cacheKey]: asset.status! }));
            }
            const fullBleedElement: ImageItem | VideoItem = isVideo
                ? ({
                      id: uuidv4(),
                      type: "video",
                      name: file.name,
                      x: 0,
                      y: 0,
                      width: dimensions.width,
                      height: dimensions.height,
                      videoUrl: asset.url,
                      cacheKey: asset.cacheKey,
                      mimeType: asset.mimeType as VideoMimeType,
                      sizeBytes: asset.sizeBytes,
                      muted: true,
                      loop: false,
                  } as VideoItem)
                : ({
                      id: uuidv4(),
                      type: "image",
                      name: file.name,
                      x: 0,
                      y: 0,
                      width: dimensions.width,
                      height: dimensions.height,
                      imageUrl: asset.url,
                      cacheKey: asset.cacheKey,
                      mimeType: asset.mimeType,
                      sizeBytes: asset.sizeBytes,
                  } as ImageItem);
            updateScene(activeIndex, (scene) => ({
                ...scene,
                elements: [
                    fullBleedElement,
                    ...scene.elements.filter((el) => !isPrimarySceneMedia(el)),
                ],
            }));
        } catch (error) {
            console.error("upload failed", error);
            toast.error(t("templates.upload.error", { defaultValue: "Upload failed" }));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemoveMedia = () => {
        updateScene(activeIndex, (scene) => ({
            ...scene,
            elements: scene.elements.filter((el) => !isPrimarySceneMedia(el)),
        }));
    };

    const handleAddWebsite = () => {
        if (!activeScene) return;
        const normalizedUrl = normalizeWebsiteUrl(websiteUrl);
        if (!normalizedUrl) {
            toast.error(t("templates.editor.invalidWebsiteUrl"));
            return;
        }

        const websiteElement = createWebsiteElement(normalizedUrl, dimensions);

        updateScene(activeIndex, (scene) => ({
            ...scene,
            elements: [websiteElement, ...scene.elements.filter((el) => !isPrimarySceneMedia(el))],
        }));
        setWebsiteUrl("");
    };

    const handleSave = () => {
        const pendingWebsiteUrl = websiteUrl.trim();
        const normalizedWebsiteUrl = pendingWebsiteUrl
            ? normalizeWebsiteUrl(pendingWebsiteUrl)
            : null;
        if (pendingWebsiteUrl && !normalizedWebsiteUrl) {
            toast.error(t("templates.editor.invalidWebsiteUrl"));
            return;
        }
        const effectiveActiveScenes = normalizedWebsiteUrl
            ? scenes.map((scene, index) =>
                  index === activeIndex
                      ? {
                            ...scene,
                            elements: [
                                createWebsiteElement(normalizedWebsiteUrl, dimensions),
                                ...scene.elements.filter((el) => !isPrimarySceneMedia(el)),
                            ],
                        }
                      : scene,
              )
            : scenes;

        if (normalizedWebsiteUrl) {
            setActiveScenes(() => effectiveActiveScenes);
            setWebsiteUrl("");
        }

        const nextPrimary = isEditingPrimary
            ? { ...primary, scenes: effectiveActiveScenes }
            : primary;
        const nextAlternate =
            !isEditingPrimary && alternate
                ? { ...alternate, scenes: effectiveActiveScenes }
                : alternate;
        const fallbackStyle: TemplateStyle = {
            textColor: "",
            backgroundColor: "",
            cardBackgroundColor: "",
        };
        const sceneToStore: TemplateSceneV2 = {
            ...initialScene,
            orientation: nextPrimary.orientation,
            dimensions: nextPrimary.dimensions,
            scenes: nextPrimary.scenes,
            defaultPhotoDurationMs: defaultDuration,
            style: {
                ...fallbackStyle,
                ...initialScene.style,
                accentColor,
            },
            checksum: "",
            alternate: nextAlternate
                ? {
                      orientation: nextAlternate.orientation,
                      dimensions: nextAlternate.dimensions,
                      scenes: nextAlternate.scenes,
                  }
                : undefined,
        };
        onSave({
            ...template,
            name,
            scene: sceneToStore,
        });
    };

    const aspectRatio = dimensions.width / Math.max(1, dimensions.height);
    const orientationLabel = (orientation: TemplateOrientation) =>
        t(`templates.editor.orientation.${orientation}`, {
            defaultValue: orientation === "horizontal" ? "Horizontal" : "Vertical",
        });
    const otherOrientationLabel = orientationLabel(oppositeOrientation(primary.orientation));

    return (
        <div className="flex flex-col h-full min-h-0 gap-3">
            {/* Top bar */}
            <div className="flex items-center gap-3 flex-wrap">
                <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("templates.editor.name", { defaultValue: "Template name" })}
                    className="max-w-sm"
                />
                <span className="text-xs text-muted-foreground tabular-nums">
                    v2 · {orientationLabel(activeOrientation)} · {dimensions.width}×
                    {dimensions.height}
                </span>
                <div className="flex items-center gap-1">
                    <Button
                        size="sm"
                        variant={isEditingPrimary ? "default" : "outline"}
                        onClick={() => switchOrientation(primary.orientation)}
                    >
                        {orientationLabel(primary.orientation)}
                    </Button>
                    {alternate ? (
                        <>
                            <Button
                                size="sm"
                                variant={!isEditingPrimary ? "default" : "outline"}
                                onClick={() => switchOrientation(alternate.orientation)}
                            >
                                {orientationLabel(alternate.orientation)}
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={handleRemoveVersion}
                                title={t("templates.editor.removeVersion", {
                                    defaultValue: "Remove this version",
                                })}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </>
                    ) : (
                        <Button size="sm" variant="outline" onClick={handleAddVersion}>
                            <Plus className="w-4 h-4 mr-1" />
                            {t("templates.editor.addVersion", {
                                defaultValue: `Add ${otherOrientationLabel} version`,
                                orientation: otherOrientationLabel,
                            })}
                        </Button>
                    )}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <Label htmlFor="default-duration" className="text-xs whitespace-nowrap">
                        {t("templates.editor.defaultDuration", {
                            defaultValue: "Default photo duration",
                        })}
                    </Label>
                    <Input
                        id="default-duration"
                        type="number"
                        min={500}
                        step={500}
                        value={defaultDuration}
                        onChange={(e) =>
                            setDefaultDuration(Math.max(500, Number(e.target.value) || 0))
                        }
                        className="w-24"
                    />
                    <span className="text-xs text-muted-foreground">ms</span>
                    <AccentColorField value={accentColor} onChange={setAccentColor} />
                    <Button onClick={handleSave}>
                        {t("general.save", { defaultValue: "Save" })}
                    </Button>
                </div>
            </div>

            {/* Main area: canvas + inspector */}
            <div className="flex flex-1 min-h-0 gap-3">
                <div className="flex-1 min-w-0 flex items-center justify-center">
                    <SceneCanvas
                        scene={activeScene}
                        dimensions={dimensions}
                        aspectRatio={aspectRatio}
                        uploading={uploading}
                        assetStatus={assetStatus}
                        onPickMedia={triggerFilePicker}
                    />
                </div>

                <SceneInspector
                    activeScene={activeScene}
                    defaultDuration={defaultDuration}
                    uploading={uploading}
                    onUploadClick={triggerFilePicker}
                    websiteUrl={websiteUrl}
                    onWebsiteUrlChange={setWebsiteUrl}
                    onAddWebsite={handleAddWebsite}
                    onRemoveMedia={handleRemoveMedia}
                    onDurationChange={(value) =>
                        updateScene(activeIndex, (scene) => ({
                            ...scene,
                            durationMs: value,
                        }))
                    }
                    onSceneNameChange={(value) =>
                        updateScene(activeIndex, (scene) => ({ ...scene, name: value }))
                    }
                />

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,video/*"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUploadMedia(file);
                    }}
                />
            </div>

            {/* Bottom: scene strip */}
            <Card className="p-2">
                <div className="flex items-center gap-2 overflow-x-auto">
                    {scenes.map((scene, i) => (
                        <SceneTile
                            key={scene.id}
                            scene={scene}
                            index={i}
                            active={i === activeIndex}
                            canMoveUp={i > 0}
                            canMoveDown={i < scenes.length - 1}
                            onSelect={() => setActiveIndex(i)}
                            onRemove={() => handleRemoveScene(i)}
                            onMove={(dir) => handleReorder(i, dir === "up" ? i - 1 : i + 1)}
                        />
                    ))}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddScene}
                        className="h-[88px] min-w-[80px] flex-col gap-1 shrink-0"
                    >
                        <Plus className="w-5 h-5" />
                        <span className="text-xs">
                            {t("templates.editor.addScene", { defaultValue: "Add scene" })}
                        </span>
                    </Button>
                </div>
            </Card>
        </div>
    );
};

const SceneCanvas = ({
    scene,
    dimensions,
    aspectRatio,
    uploading,
    assetStatus,
    onPickMedia,
}: {
    scene: SceneV2 | undefined;
    dimensions: { width: number; height: number };
    aspectRatio: number;
    uploading: boolean;
    assetStatus: Record<string, AssetStatus>;
    onPickMedia: () => void;
}) => {
    const { t } = useTranslation();
    const media = scene?.elements.find((el): el is ImageItem | VideoItem | WebsiteItem =>
        isPrimarySceneMedia(el),
    );
    const mediaStatus =
        media?.type === "video" && (media as VideoItem).cacheKey
            ? assetStatus[(media as VideoItem).cacheKey]
            : "ready";
    const isProcessing = mediaStatus === "processing";
    const isFailed = mediaStatus === "failed";

    // Cap the preview to viewport-driven dimensions so vertical templates don't overflow.
    // We compute style based on aspect ratio: portrait gets height-bounded, landscape width-bounded.
    const style: React.CSSProperties =
        aspectRatio < 1
            ? { height: "100%", width: "auto", aspectRatio }
            : { width: "100%", maxWidth: "100%", aspectRatio };

    return (
        <div
            className={cn(
                "relative bg-muted rounded-lg border border-dashed border-border overflow-hidden",
                "flex items-center justify-center select-none",
                "max-h-full",
                !media && "cursor-pointer hover:border-primary hover:bg-muted/60 transition-colors",
            )}
            style={style}
            onClick={!media && !uploading ? onPickMedia : undefined}
        >
            {!media ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground p-6 text-center">
                    {uploading ? (
                        <>
                            <Upload className="w-8 h-8 animate-pulse" />
                            <span className="text-sm">{t("templates.editor.uploading")}</span>
                        </>
                    ) : (
                        <>
                            <Upload className="w-8 h-8" />
                            <span className="text-sm font-medium">
                                {t("templates.editor.clickToUpload")}
                            </span>
                            <span className="text-xs">
                                {dimensions.width}×{dimensions.height} · jpg, png, gif, mp4, webm
                            </span>
                        </>
                    )}
                </div>
            ) : media.type === "video" && isProcessing ? (
                <div className="flex flex-col items-center gap-2 text-muted-foreground p-6 text-center">
                    <Film className="w-8 h-8 animate-pulse" />
                    <span className="text-sm font-medium">
                        {t("templates.editor.processingVideo")}
                    </span>
                    <span className="text-xs">{t("templates.editor.processingVideoHint")}</span>
                </div>
            ) : media.type === "video" && isFailed ? (
                <div className="flex flex-col items-center gap-2 text-destructive p-6 text-center">
                    <Film className="w-8 h-8" />
                    <span className="text-sm font-medium">{t("templates.editor.videoFailed")}</span>
                    <span className="text-xs text-muted-foreground">
                        {t("templates.editor.videoFailedHint")}
                    </span>
                </div>
            ) : media.type === "video" ? (
                <video
                    src={resolveAssetUrl((media as VideoItem).videoUrl)}
                    className="absolute inset-0 w-full h-full object-cover bg-black"
                    muted
                    autoPlay
                    loop
                    playsInline
                    controls={false}
                />
            ) : media.type === "website" ? (
                <iframe
                    src={(media as WebsiteItem).websiteUrl}
                    title={media.name}
                    className="absolute inset-0 w-full h-full border-0 bg-white"
                    sandbox="allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
                />
            ) : (
                <img
                    src={resolveAssetUrl((media as ImageItem).imageUrl)}
                    alt={media.name}
                    className="absolute inset-0 w-full h-full object-contain bg-black"
                />
            )}
        </div>
    );
};

const SceneInspector = ({
    activeScene,
    defaultDuration,
    uploading,
    onUploadClick,
    websiteUrl,
    onWebsiteUrlChange,
    onAddWebsite,
    onRemoveMedia,
    onDurationChange,
    onSceneNameChange,
}: {
    activeScene: SceneV2 | undefined;
    defaultDuration: number;
    uploading: boolean;
    onUploadClick: () => void;
    websiteUrl: string;
    onWebsiteUrlChange: (value: string) => void;
    onAddWebsite: () => void;
    onRemoveMedia: () => void;
    onDurationChange: (value: number) => void;
    onSceneNameChange: (value: string) => void;
}) => {
    const { t } = useTranslation();
    if (!activeScene) {
        return null;
    }
    const media = activeScene.elements.find((el): el is ImageItem | VideoItem | WebsiteItem =>
        isPrimarySceneMedia(el),
    );
    const isVideo = media?.type === "video";
    const isWebsite = media?.type === "website";

    return (
        <Card className="w-72 shrink-0 flex flex-col gap-3 p-4 overflow-y-auto">
            <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("templates.scene")}
                </Label>
                <Input
                    value={activeScene.name}
                    onChange={(e) => onSceneNameChange(e.target.value)}
                    className="mt-1"
                />
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="scene-duration" className="text-xs">
                    {t("templates.editor.sceneDuration", {
                        defaultValue: "Scene duration (ms)",
                    })}
                </Label>
                <Input
                    id="scene-duration"
                    type="number"
                    min={500}
                    step={500}
                    value={activeScene.durationMs ?? defaultDuration}
                    onChange={(e) => onDurationChange(Math.max(500, Number(e.target.value) || 0))}
                />
                <p className="text-xs text-muted-foreground">
                    {isVideo
                        ? t("templates.editor.sceneDurationVideoHint")
                        : t("templates.editor.sceneDurationHint")}
                </p>
            </div>

            <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t("templates.editor.media")}
                </Label>
                {media ? (
                    <div className="flex items-center gap-2 rounded border p-2 bg-muted/40">
                        {isVideo ? (
                            <Film className="w-4 h-4 text-muted-foreground" />
                        ) : isWebsite ? (
                            <Globe2 className="w-4 h-4 text-muted-foreground" />
                        ) : (
                            <ImageIcon className="w-4 h-4 text-muted-foreground" />
                        )}
                        <span className="text-xs flex-1 truncate" title={media.name}>
                            {media.name}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={onRemoveMedia}
                        >
                            <Trash2 className="w-3 h-3" />
                        </Button>
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground">{t("templates.editor.noMedia")}</p>
                )}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onUploadClick}
                    disabled={uploading}
                    className="w-full"
                >
                    <Upload className="w-3.5 h-3.5 mr-2" />
                    {uploading
                        ? t("templates.editor.uploading")
                        : media
                          ? t("templates.editor.replaceMedia")
                          : t("templates.editor.uploadMedia")}
                </Button>
                <div className="space-y-1.5 pt-2">
                    <Label htmlFor="website-url" className="text-xs">
                        {t("templates.editor.websiteUrl")}
                    </Label>
                    <div className="flex gap-2">
                        <Input
                            id="website-url"
                            type="url"
                            value={websiteUrl}
                            onChange={(e) => onWebsiteUrlChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    onAddWebsite();
                                }
                            }}
                            placeholder="https://example.com"
                        />
                        <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={onAddWebsite}
                            aria-label={t("templates.editor.addWebsite")}
                        >
                            <Link className="h-4 w-4" />
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {t("templates.editor.websiteHint")}
                    </p>
                </div>
            </div>
        </Card>
    );
};

const SceneTile = ({
    scene,
    index,
    active,
    canMoveUp,
    canMoveDown,
    onSelect,
    onRemove,
    onMove,
}: {
    scene: SceneV2;
    index: number;
    active: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onSelect: () => void;
    onRemove: () => void;
    onMove: (dir: "up" | "down") => void;
}) => {
    const { t } = useTranslation();
    const media = scene.elements.find((el): el is ImageItem | VideoItem | WebsiteItem =>
        isPrimarySceneMedia(el),
    );
    const previewUrl = media
        ? media.type === "video"
            ? null
            : media.type === "website"
              ? null
              : resolveAssetUrl((media as ImageItem).imageUrl)
        : null;
    const isVideo = media?.type === "video";
    const isWebsite = media?.type === "website";

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onSelect}
            className={cn(
                "relative rounded-md border shrink-0 cursor-pointer overflow-hidden",
                "w-[120px] h-[88px] flex flex-col",
                active ? "ring-2 ring-primary border-primary" : "border-border",
            )}
        >
            {previewUrl ? (
                <img
                    src={previewUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                />
            ) : isVideo ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white">
                    <Film className="w-6 h-6" />
                </div>
            ) : isWebsite ? (
                <div className="absolute inset-0 flex items-center justify-center bg-sky-950 text-white">
                    <Globe2 className="w-6 h-6" />
                </div>
            ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
                    <ImageIcon className="w-5 h-5" />
                </div>
            )}
            <div className="absolute top-1 left-1 text-[10px] font-mono bg-background/90 rounded px-1.5 py-0.5">
                #{index + 1}
            </div>
            <div className="absolute top-1 right-1 flex items-center gap-0.5">
                <button
                    type="button"
                    disabled={!canMoveUp}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMove("up");
                    }}
                    className="bg-background/90 hover:bg-background rounded p-0.5 disabled:opacity-30"
                    aria-label={t("templates.editor.moveLeft")}
                >
                    <ChevronLeft className="w-3 h-3" />
                </button>
                <button
                    type="button"
                    disabled={!canMoveDown}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMove("down");
                    }}
                    className="bg-background/90 hover:bg-background rounded p-0.5 disabled:opacity-30"
                    aria-label={t("templates.editor.moveRight")}
                >
                    <ChevronRight className="w-3 h-3" />
                </button>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="bg-background/90 hover:bg-destructive hover:text-destructive-foreground rounded p-0.5"
                    aria-label={t("templates.editor.removeScene")}
                >
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px] truncate px-1.5 py-1">
                {scene.name}
            </div>
        </div>
    );
};

/** Returns true when a layer is the scene's primary full-bleed media source. */
function isPrimarySceneMedia(layer: LayerItem): layer is ImageItem | VideoItem | WebsiteItem {
    return layer.type === "image" || layer.type === "video" || layer.type === "website";
}

/**
 * Normalizes editor URL input into an absolute http(s) URL that presenters can render.
 */
function normalizeWebsiteUrl(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

    try {
        const parsed = new URL(withScheme);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return null;
        }
        return parsed.toString();
    } catch {
        return null;
    }
}

/**
 * Builds a full-bleed website layer for the current slideshow design dimensions.
 */
function createWebsiteElement(
    websiteUrl: string,
    dimensions: TemplateSceneV2["dimensions"],
): WebsiteItem {
    return {
        id: uuidv4(),
        type: "website",
        name: websiteUrl,
        x: 0,
        y: 0,
        width: dimensions.width,
        height: dimensions.height,
        websiteUrl,
    };
}

/**
 * Resolves presenter asset URLs for previews shown in the management UI.
 */
function resolveAssetUrl(url: string): string {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${import.meta.env.VITE_API_URL ?? ""}${url}`;
}

export type _UnusedLayerItem = LayerItem;
